import { Link } from "react-router-dom";
import { CheckCircle2, XCircle, Phone, Scale, Calculator, ArrowRight } from "lucide-react";
import { SiteLayout } from "@/components/site/SiteLayout";
import { PageSEO } from "@/components/site/PageSEO";
import { ResourceHero } from "@/components/site/ResourceHero";
import { QuickAnswer } from "@/components/site/QuickAnswer";
import { ReviewedBy } from "@/components/site/ReviewedBy";
import { CTASection } from "@/components/site/CTASection";
import { brand, founder } from "@/data/site/brand";
import { stockImages } from "@/data/site/stockImages";

const lastReviewed = "2026-06-12";
const datePublished = "2026-06-12";

const QUICK_ANSWER =
  "Both CPAs and tax attorneys can represent you before the IRS, but they solve different problems. A CPA is the right choice when the case is financial — Offers in Compromise, installment agreements, payroll-tax workouts, audits, unfiled returns, and most collection cases. A tax attorney is the right choice when there is criminal exposure, when the case is heading to U.S. Tax Court litigation, or when attorney-client privilege over communications is essential. For the typical IRS collection or audit case, hiring a CPA who specializes in tax resolution is faster, less expensive, and produces the same authority to represent you.";

const comparisonRows: { area: string; cpa: string; attorney: string }[] = [
  {
    area: "IRS representation authority",
    cpa: "Full — Form 2848 Power of Attorney, all 50 states, all IRS divisions",
    attorney: "Full — same Form 2848 authority",
  },
  {
    area: "Offer in Compromise / installment agreements",
    cpa: "Primary practitioner — financial-analysis driven",
    attorney: "Can do it, but usually outsources financials",
  },
  {
    area: "IRS audits (correspondence, office, field)",
    cpa: "Primary practitioner — books, records, and substantiation are the case",
    attorney: "Appropriate when fraud or criminal referral is suspected",
  },
  {
    area: "Payroll tax / Trust Fund Recovery Penalty",
    cpa: "Primary practitioner — Form 4180 interviews, allocation, abatement",
    attorney: "Appropriate if TFRP leads to criminal exposure",
  },
  {
    area: "Unfiled returns / SFR reconstruction",
    cpa: "Primary practitioner — they actually prepare the returns",
    attorney: "Cannot prepare returns; refers out to a CPA",
  },
  {
    area: "U.S. Tax Court litigation",
    cpa: "Cannot litigate (unless USTCP-admitted)",
    attorney: "Primary practitioner",
  },
  {
    area: "Criminal tax defense / IRS-CI investigation",
    cpa: "Should refer to a tax attorney immediately",
    attorney: "Primary practitioner — attorney-client privilege applies",
  },
  {
    area: "Attorney-client privilege",
    cpa: "Limited §7525 privilege only; does not cover criminal matters",
    attorney: "Full attorney-client privilege",
  },
  {
    area: "Typical fee structure",
    cpa: "Flat fee tied to scope; generally lower",
    attorney: "Hourly or retainer; generally higher",
  },
];

const cpaBetterFor = [
  "You owe back taxes and want an Offer in Compromise or installment agreement.",
  "You have unfiled returns the IRS is pressuring you to file (or already filed SFRs).",
  "You received a CP14, CP501, CP503, CP504, LT11, or CP90 collection notice.",
  "Your wages are being garnished or your bank account was levied.",
  "You're being audited and need someone who understands your books, not your courtroom strategy.",
  "You own a business with payroll-tax (941) liabilities or a Trust Fund Recovery assessment.",
  "You're a non-filer trying to get current and into compliance.",
];

const attorneyBetterFor = [
  "The IRS Criminal Investigation Division (IRS-CI) has contacted you.",
  "You're under investigation for tax evasion, willful failure to file, or fraud.",
  "Your case is going to U.S. Tax Court and will be litigated.",
  "There are foreign accounts with willful non-disclosure (offshore voluntary disclosure).",
  "You need attorney-client privilege over communications because criminal exposure is realistic.",
  "A grand jury subpoena has been issued in connection with your tax matter.",
];

const myths = [
  {
    myth: '"Only a tax attorney can represent me before the IRS."',
    truth:
      "False. CPAs, enrolled agents, and attorneys all have unlimited practice rights before the IRS under Circular 230. The Form 2848 Power of Attorney works the same for all three.",
  },
  {
    myth: '"A tax attorney will get me a better Offer in Compromise."',
    truth:
      "No. An OIC is decided by Form 433-A(OIC)/433-B(OIC) financials and IRS Allowable Living Expense standards. The work is forensic accounting, not legal argument. A CPA who specializes in OICs is the natural fit.",
  },
  {
    myth: '"I need an attorney because there\'s a tax lien."',
    truth:
      "A Notice of Federal Tax Lien is a collection action, not a lawsuit. Lien withdrawal, discharge, and subordination are all administrative requests handled by CPAs and EAs every day.",
  },
  {
    myth: '"Attorney-client privilege protects everything I tell my tax attorney."',
    truth:
      "Only when the attorney is providing legal advice. If the attorney is preparing returns or doing routine tax work, privilege does not apply — same as with a CPA.",
  },
];

const CpaVsTaxAttorney = () => {
  const jsonLd: object[] = [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline:
        "CPA vs. Tax Attorney for IRS Help — Who You Actually Need (2026)",
      description: QUICK_ANSWER.slice(0, 200),
      datePublished,
      dateModified: lastReviewed,
      author: {
        "@type": "Person",
        name: founder.name,
        jobTitle: founder.title,
        url: `${brand.siteUrl}/about/franklin-sofi`,
      },
      reviewedBy: {
        "@type": "Person",
        name: founder.name,
        jobTitle: founder.title,
        url: `${brand.siteUrl}/about/franklin-sofi`,
      },
      publisher: {
        "@type": "Organization",
        name: brand.legalName,
        url: brand.siteUrl,
      },
      mainEntityOfPage: `${brand.siteUrl}/resources/cpa-vs-tax-attorney-for-irs-help`,
      inLanguage: "en-US",
      speakable: {
        "@type": "SpeakableSpecification",
        cssSelector: [".speakable", "h1", "h2"],
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name: "Can a CPA represent me before the IRS?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Yes. CPAs have unlimited practice rights before the IRS under Circular 230 and can represent taxpayers in all 50 states for collections, audits, appeals, and settlements using Form 2848 Power of Attorney.",
          },
        },
        {
          "@type": "Question",
          name: "When do I need a tax attorney instead of a CPA?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Hire a tax attorney when there is criminal tax exposure, when the case is going to U.S. Tax Court litigation, when IRS Criminal Investigation has contacted you, or when full attorney-client privilege over communications is essential.",
          },
        },
        {
          "@type": "Question",
          name: "Is a CPA cheaper than a tax attorney?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Generally yes. CPAs typically quote flat fees tied to the scope of work, while tax attorneys typically bill hourly or on retainer. For routine collection and audit cases, a CPA is the more cost-effective choice.",
          },
        },
        {
          "@type": "Question",
          name: "Does attorney-client privilege apply to tax matters?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Attorney-client privilege only applies when the attorney is providing legal advice — not routine tax preparation. CPAs have a limited §7525 federally-authorized tax practitioner privilege that covers tax-advice communications but does not extend to criminal matters.",
          },
        },
      ],
    },
  ];

  return (
    <SiteLayout>
      <PageSEO
        title="CPA vs. Tax Attorney for IRS Help — Who You Actually Need"
        description="A licensed CPA explains when to hire a CPA vs. a tax attorney for IRS problems — collections, audits, OICs, payroll tax, and criminal cases."
        path="/resources/cpa-vs-tax-attorney-for-irs-help"
        image={stockImages.federalColumns.src}
        ogType="article"
        author={founder.name}
        publishedTime={datePublished}
        modifiedTime={lastReviewed}
        breadcrumbs={[
          { name: "Home", href: "/" },
          { name: "Resources", href: "/resources" },
          { name: "CPA vs. Tax Attorney", href: "/resources/cpa-vs-tax-attorney-for-irs-help" },
        ]}
        jsonLd={jsonLd}
      />

      <ResourceHero
        breadcrumbs={[
          { name: "Home", href: "/" },
          { name: "Resources", href: "/resources" },
          { name: "CPA vs. Tax Attorney" },
        ]}
        eyebrow="Decision guide"
        title="CPA vs. Tax Attorney for IRS Help"
        summary="Both can represent you before the IRS. Most cases need a CPA. A few need an attorney. Here's how to tell which one you actually need — and why the difference matters for cost, speed, and outcome."
        facts={[
          { label: "Last reviewed", value: "Jun 2026" },
          { label: "Reviewed by", value: founder.name },
          { label: "Read time", value: "8 min" },
        ]}
      />

      <section className="py-12 lg:py-16 bg-white">
        <div className="container mx-auto px-4 max-w-4xl">
          <QuickAnswer
            heading="CPA or tax attorney — who do I need for my IRS problem?"
            answer={QUICK_ANSWER}
          />

          <ReviewedBy date={lastReviewed} />

          {/* Two-card decision */}
          <div className="grid md:grid-cols-2 gap-6 mt-12">
            <div className="rounded-2xl border-2 border-gold/40 bg-gold/5 p-6 lg:p-8">
              <div className="flex items-center gap-3 mb-3">
                <Calculator className="w-7 h-7 text-gold-dark" />
                <h2 className="font-heading text-2xl font-bold text-navy-dark m-0">
                  Hire a CPA when…
                </h2>
              </div>
              <ul className="space-y-2.5 mt-4">
                {cpaBetterFor.map((item) => (
                  <li key={item} className="flex gap-2.5 text-[15px] text-navy-dark/85 leading-relaxed">
                    <CheckCircle2 className="w-4 h-4 text-gold-dark mt-1 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-2xl border border-navy-dark/10 bg-secondary/40 p-6 lg:p-8">
              <div className="flex items-center gap-3 mb-3">
                <Scale className="w-7 h-7 text-navy" />
                <h2 className="font-heading text-2xl font-bold text-navy-dark m-0">
                  Hire a tax attorney when…
                </h2>
              </div>
              <ul className="space-y-2.5 mt-4">
                {attorneyBetterFor.map((item) => (
                  <li key={item} className="flex gap-2.5 text-[15px] text-navy-dark/85 leading-relaxed">
                    <CheckCircle2 className="w-4 h-4 text-navy mt-1 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Side-by-side comparison table */}
          <h2 className="font-heading text-3xl font-bold text-navy-dark mt-16 mb-6">
            Side-by-side: CPA vs. tax attorney for IRS work
          </h2>
          <div className="overflow-x-auto rounded-xl border border-border">
            <table className="w-full text-sm">
              <thead className="bg-secondary/60">
                <tr className="text-left">
                  <th className="p-4 font-semibold text-navy-dark">Issue</th>
                  <th className="p-4 font-semibold text-navy-dark">CPA (tax resolution)</th>
                  <th className="p-4 font-semibold text-navy-dark">Tax attorney</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {comparisonRows.map((row) => (
                  <tr key={row.area} className="align-top">
                    <td className="p-4 font-semibold text-navy-dark">{row.area}</td>
                    <td className="p-4 text-navy-dark/85">{row.cpa}</td>
                    <td className="p-4 text-navy-dark/85">{row.attorney}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Authority section */}
          <h2 className="font-heading text-3xl font-bold text-navy-dark mt-16 mb-4">
            Both have the same authority to represent you
          </h2>
          <p className="text-[16px] text-navy-dark/85 leading-relaxed">
            Under <a href="https://www.irs.gov/tax-professionals/circular-230-tax-professionals" target="_blank" rel="noopener noreferrer" className="text-navy underline">IRS Circular 230</a>, CPAs, attorneys, and Enrolled Agents all have <strong>unlimited practice rights</strong> before the IRS. That means any of the three can:
          </p>
          <ul className="space-y-2 mt-4 text-[15px] text-navy-dark/85">
            <li className="flex gap-2.5"><CheckCircle2 className="w-4 h-4 text-gold-dark mt-1 flex-shrink-0" />File Form 2848 Power of Attorney and speak to the IRS on your behalf.</li>
            <li className="flex gap-2.5"><CheckCircle2 className="w-4 h-4 text-gold-dark mt-1 flex-shrink-0" />Negotiate Offers in Compromise, installment agreements, and Currently Not Collectible status.</li>
            <li className="flex gap-2.5"><CheckCircle2 className="w-4 h-4 text-gold-dark mt-1 flex-shrink-0" />Defend audits at the correspondence, office, and field level.</li>
            <li className="flex gap-2.5"><CheckCircle2 className="w-4 h-4 text-gold-dark mt-1 flex-shrink-0" />Represent you in IRS Appeals.</li>
            <li className="flex gap-2.5"><CheckCircle2 className="w-4 h-4 text-gold-dark mt-1 flex-shrink-0" />Request lien withdrawals, levy releases, and penalty abatements.</li>
          </ul>
          <p className="text-[16px] text-navy-dark/85 leading-relaxed mt-4">
            The right question is not <em>"which credential is allowed?"</em> — they all are. The right question is <em>"which credential fits the work?"</em>
          </p>

          {/* Why CPA wins financial cases */}
          <h2 className="font-heading text-3xl font-bold text-navy-dark mt-16 mb-4">
            Why a CPA is usually the better fit for IRS resolution
          </h2>
          <p className="text-[16px] text-navy-dark/85 leading-relaxed">
            The overwhelming majority of IRS resolution cases — Offers in Compromise, installment agreements, audits, payroll tax workouts, unfiled returns — are won or lost on <strong>financial analysis</strong>, not legal argument. The IRS uses Form 433-A, Form 433-B, Form 433-F, and its Allowable Living Expense standards to decide what a taxpayer can pay. That work is forensic accounting:
          </p>
          <ul className="space-y-2 mt-4 text-[15px] text-navy-dark/85">
            <li className="flex gap-2.5"><CheckCircle2 className="w-4 h-4 text-gold-dark mt-1 flex-shrink-0" />Reading bank statements, P&amp;Ls, and balance sheets the way an IRS Revenue Officer reads them.</li>
            <li className="flex gap-2.5"><CheckCircle2 className="w-4 h-4 text-gold-dark mt-1 flex-shrink-0" />Building a defensible Reasonable Collection Potential (RCP) number.</li>
            <li className="flex gap-2.5"><CheckCircle2 className="w-4 h-4 text-gold-dark mt-1 flex-shrink-0" />Reconstructing books for unfiled years so returns can actually be filed.</li>
            <li className="flex gap-2.5"><CheckCircle2 className="w-4 h-4 text-gold-dark mt-1 flex-shrink-0" />Documenting substantiation for audit adjustments line by line.</li>
          </ul>
          <p className="text-[16px] text-navy-dark/85 leading-relaxed mt-4">
            That's what CPAs are trained to do every day. Tax attorneys can do it — but most outsource the financials to a CPA anyway, then bill you for the coordination.
          </p>

          {/* Common myths */}
          <h2 className="font-heading text-3xl font-bold text-navy-dark mt-16 mb-6">
            Common myths
          </h2>
          <div className="space-y-5">
            {myths.map((m) => (
              <div key={m.myth} className="rounded-xl border border-border bg-white p-5 lg:p-6">
                <p className="flex gap-2 items-start text-navy-dark font-semibold m-0">
                  <XCircle className="w-5 h-5 text-destructive mt-0.5 flex-shrink-0" /> {m.myth}
                </p>
                <p className="text-[15px] text-navy-dark/85 leading-relaxed mt-2 mb-0 pl-7">
                  {m.truth}
                </p>
              </div>
            ))}
          </div>

          {/* Bottom line */}
          <h2 className="font-heading text-3xl font-bold text-navy-dark mt-16 mb-4">
            The bottom line
          </h2>
          <p className="text-[16px] text-navy-dark/85 leading-relaxed">
            If your case is <strong>financial</strong> — back taxes, collections, audits, OIC, payroll tax — start with a CPA who specializes in tax resolution. You'll get the same authority to represent you, at a lower cost, with someone who actually does the numbers. If your case is <strong>criminal or headed to Tax Court</strong>, start with a tax attorney. If you're not sure which bucket you're in, a 15-minute call with a CPA will tell you — and a credentialed CPA is ethically obligated to refer you to a tax attorney the moment criminal exposure becomes realistic.
          </p>

          {/* Related services CTA */}
          <div className="mt-12 grid sm:grid-cols-2 gap-4">
            <Link to="/services" className="group flex items-center justify-between rounded-xl border border-border bg-white p-5 hover:border-gold hover:shadow-md transition">
              <span className="font-semibold text-navy-dark">Browse tax resolution services</span>
              <ArrowRight className="w-4 h-4 text-gold-dark group-hover:translate-x-1 transition" />
            </Link>
            <Link to="/about/franklin-sofi" className="group flex items-center justify-between rounded-xl border border-border bg-white p-5 hover:border-gold hover:shadow-md transition">
              <span className="font-semibold text-navy-dark">Meet Franklin Sofi, CPA</span>
              <ArrowRight className="w-4 h-4 text-gold-dark group-hover:translate-x-1 transition" />
            </Link>
          </div>

          <div className="mt-8 rounded-xl bg-navy-dark text-white p-6 lg:p-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-gold mb-1">
                Not sure which one you need?
              </p>
              <p className="m-0 text-white/90">
                Call a CPA. If your case needs an attorney, we'll tell you — for free.
              </p>
            </div>
            <a
              href={`tel:${brand.phoneTel}`}
              className="inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-light text-navy-dark font-bold px-6 py-3 rounded-lg whitespace-nowrap"
            >
              <Phone className="w-4 h-4" /> {brand.phone}
            </a>
          </div>
        </div>
      </section>

      <CTASection />
    </SiteLayout>
  );
};

export default CpaVsTaxAttorney;
